import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosConfig';
import { MdArrowBack, MdCloudUpload, MdCheckCircle, MdError, MdDelete, MdSave, MdTranslate, MdAutoAwesome } from 'react-icons/md';
import { SUPPORTED_LANGUAGES, translateText } from '../../utils/translator';

const EditNaiNews = ({ newsId, onBack, onUpdate }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState('');
    const [translations, setTranslations] = useState({});
    const [activeLangTab, setActiveLangTab] = useState('ta');
    const [translatingTab, setTranslatingTab] = useState(false);
    const [currentMainImage, setCurrentMainImage] = useState(null);
    const [newMainImageFile, setNewMainImageFile] = useState(null);
    const [newMainImagePreview, setNewMainImagePreview] = useState(null);
    const [currentImages, setCurrentImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null); // 'success' | 'error' | null

    useEffect(() => {
        fetchNewsDetails();
    }, [newsId]);

    const fetchNewsDetails = async () => {
        try {
            const response = await axios.get(`nai-news/${newsId}/`);
            const data = response.data;
            setTitle(data.title);
            setDescription(data.description);
            setTranslations(data.translations || {});
            setCurrentMainImage(data.image || null);
            setNewMainImageFile(null);
            setNewMainImagePreview(null);
            // Format date for input type="date"
            const formattedDate = data.created_at ? data.created_at.split('T')[0] : '';
            setDate(formattedDate);
            setCurrentImages(data.images || []);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching news details:", error);
            setLoading(false);
        }
    };

    const handleMainImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setNewMainImageFile(file);
            setNewMainImagePreview(URL.createObjectURL(file));
        }
    };

    const handleCancelNewMainImage = () => {
        setNewMainImageFile(null);
        if (newMainImagePreview) {
            URL.revokeObjectURL(newMainImagePreview);
            setNewMainImagePreview(null);
        }
    };

    const handleSetAsMainImage = async (imgId, imgUrl) => {
        if (!window.confirm("Set this gallery photo as the primary featured image of this news post?")) return;
        try {
            setSaving(true);
            await axios.put(`nai-news/${newsId}/`, {
                set_main_image_id: imgId
            });
            setCurrentMainImage(imgUrl);
            setStatus('success');
            setTimeout(() => setStatus(null), 3000);
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error("Error setting main image:", err);
            alert("Failed to set as main image.");
        } finally {
            setSaving(false);
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files) {
            setNewImages((prev) => [...prev, ...Array.from(e.target.files)]);
        }
    };

    const removeNewImage = (index) => {
        setNewImages((prev) => prev.filter((_, i) => i !== index));
    };

    const deleteExistingImage = async (imageId) => {
        if (!window.confirm("Are you sure you want to delete this image?")) return;
        
        try {
            await axios.delete(`nai-news/image/${imageId}/delete/`);
            // Remove from local state
            setCurrentImages((prev) => prev.filter((img) => img.id !== imageId));
        } catch (error) {
            console.error("Error deleting image:", error);
            alert("Failed to delete image.");
        }
    };

    const handleTranslationChange = (langCode, field, value) => {
        setTranslations((prev) => ({
            ...prev,
            [langCode]: {
                ...prev[langCode],
                [field]: value,
            },
        }));
    };

    const handleAutoTranslateCurrentTab = async () => {
        if (!title && !description) {
            alert("Please enter title or description first before auto-translating.");
            return;
        }

        try {
            setTranslatingTab(true);
            const [transTitle, transDesc] = await Promise.all([
                title ? translateText(title, activeLangTab) : Promise.resolve(''),
                description ? translateText(description, activeLangTab) : Promise.resolve(''),
            ]);

            setTranslations((prev) => ({
                ...prev,
                [activeLangTab]: {
                    title: transTitle,
                    description: transDesc,
                },
            }));
        } catch (err) {
            console.error("Auto-translate error:", err);
            alert("Failed to auto-translate. Please try again or type manually.");
        } finally {
            setTranslatingTab(false);
        }
    };

    const handleClearTranslation = (langCode) => {
        setTranslations((prev) => {
            const updated = { ...prev };
            delete updated[langCode];
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setStatus(null);

        // 1. Update News Details including translations and new main image
        try {
            const formData = new FormData();
            formData.append('title', title);
            formData.append('description', description);
            if (date) formData.append('created_at', new Date(date).toISOString());
            formData.append('translations', JSON.stringify(translations));

            if (newMainImageFile) {
                formData.append('image', newMainImageFile);
            }

            await axios.put(`nai-news/${newsId}/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // 2. Upload New Images if any
            if (newImages.length > 0) {
                const imagesData = new FormData();
                newImages.forEach((image) => {
                    imagesData.append('images', image);
                });
                await axios.post(`nai-news/${newsId}/add-images/`, imagesData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            setStatus('success');
            setNewImages([]);
            setNewMainImageFile(null);
            setNewMainImagePreview(null);
            // Refresh details to see new images
            fetchNewsDetails();
            setSaving(false);
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error("Error updating news:", error);
            setStatus('error');
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="p-6">Loading...</div>;
    }

    const currentLangInfo = SUPPORTED_LANGUAGES.find((l) => l.code === activeLangTab) || SUPPORTED_LANGUAGES[0];

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
            <div className="flex items-center mb-6">
                <button 
                    onClick={onBack}
                    className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                    <MdArrowBack className="text-gray-600 w-6 h-6" />
                </button>
                <h2 className="text-2xl font-bold text-gray-800">Edit News</h2>
            </div>

            {status === 'success' && (
                <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-md flex items-center">
                    <MdCheckCircle className="text-green-500 w-6 h-6 mr-3" />
                    <div>
                        <p className="font-medium text-green-700">Success!</p>
                        <p className="text-sm text-green-600">News updated successfully.</p>
                    </div>
                </div>
            )}

            {status === 'error' && (
                 <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md flex items-center">
                     <MdError className="text-red-500 w-6 h-6 mr-3" />
                     <div>
                        <p className="font-medium text-red-700">Error</p>
                        <p className="text-sm text-red-600">Failed to update news. Please try again.</p>
                     </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Details Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">News Details (Default / English)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Title</label>
                            <input
                                type="text"
                                required
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Publish Date</label>
                             <input
                                type="date"
                                required
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={10}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                        />
                    </div>
                </div>

                {/* Multilingual Translations Section */}
                <div className="space-y-4 border border-blue-100 rounded-xl p-5 bg-gradient-to-br from-blue-50/40 via-white to-gray-50/50 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200 pb-3">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 flex items-center">
                                <MdTranslate className="text-blue-600 mr-2 text-xl" />
                                Edit Translated Content
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Customize or review news translations for each language. If left empty, readers will automatically receive AI translation.
                            </p>
                        </div>
                    </div>

                    {/* Language Selector Tabs */}
                    <div className="flex flex-wrap gap-2 pt-1">
                        {SUPPORTED_LANGUAGES.map((lang) => {
                            const isTabActive = activeLangTab === lang.code;
                            const hasCustomData = Boolean(
                                translations[lang.code] &&
                                (translations[lang.code].title?.trim() || translations[lang.code].description?.trim())
                            );

                            return (
                                <button
                                    type="button"
                                    key={lang.code}
                                    onClick={() => setActiveLangTab(lang.code)}
                                    className={`px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 transition-all ${
                                        isTabActive
                                            ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-400'
                                            : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-blue-300'
                                    }`}
                                >
                                    <span>{lang.name}</span>
                                    <span className={isTabActive ? 'text-blue-200' : 'text-gray-400'}>
                                        ({lang.nativeName})
                                    </span>
                                    {hasCustomData && (
                                        <span className={`w-2 h-2 rounded-full ${isTabActive ? 'bg-green-300' : 'bg-green-500'}`} title="Custom content saved" />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Active Language Editor Panel */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4 shadow-sm mt-3">
                        <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-gray-100">
                            <span className="text-sm font-semibold text-gray-700">
                                {currentLangInfo.name} ({currentLangInfo.nativeName}) Content
                            </span>

                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleAutoTranslateCurrentTab}
                                    disabled={translatingTab}
                                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors disabled:opacity-60"
                                >
                                    <MdAutoAwesome className="mr-1.5 text-blue-600" />
                                    {translatingTab ? 'Translating...' : `Auto-Translate into ${currentLangInfo.name}`}
                                </button>

                                {translations[activeLangTab] && (
                                    <button
                                        type="button"
                                        onClick={() => handleClearTranslation(activeLangTab)}
                                        className="text-xs text-red-600 hover:text-red-800 px-2 py-1 hover:underline"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {currentLangInfo.name} Title
                            </label>
                            <input
                                type="text"
                                placeholder={`Enter ${currentLangInfo.name} title or click Auto-Translate`}
                                value={translations[activeLangTab]?.title || ''}
                                onChange={(e) => handleTranslationChange(activeLangTab, 'title', e.target.value)}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {currentLangInfo.name} Description
                            </label>
                            <textarea
                                rows={8}
                                placeholder={`Enter ${currentLangInfo.name} description or click Auto-Translate`}
                                value={translations[activeLangTab]?.description || ''}
                                onChange={(e) => handleTranslationChange(activeLangTab, 'description', e.target.value)}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Primary/Featured Main Image Section */}
                <div className="space-y-4 border border-gray-200 rounded-xl p-5 bg-gray-50/50 shadow-sm">
                    <div className="border-b pb-2 flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800">Featured / Main Post Image</h3>
                            <p className="text-xs text-gray-500">This is the primary image shown at the top of the news article, on news cards, and in WhatsApp previews.</p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 pt-2">
                        {/* Current or New Preview */}
                        <div className="relative w-48 h-32 bg-gray-100 rounded-lg overflow-hidden border border-gray-300 shadow-sm flex items-center justify-center">
                            {newMainImagePreview ? (
                                <img src={newMainImagePreview} alt="New Preview" className="w-full h-full object-cover" />
                            ) : currentMainImage ? (
                                <img src={currentMainImage} alt="Current Main" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-xs text-gray-400 italic">No image uploaded</span>
                            )}
                            {newMainImagePreview && (
                                <span className="absolute bottom-1 left-1 bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                                    New Selected
                                </span>
                            )}
                        </div>

                        {/* Controls */}
                        <div className="space-y-3">
                            <div>
                                <label className="inline-flex items-center px-4 py-2 border border-blue-600 rounded-md shadow-sm text-sm font-medium text-blue-600 bg-white hover:bg-blue-50 cursor-pointer transition-colors">
                                    <MdCloudUpload className="mr-2 text-lg" />
                                    {currentMainImage || newMainImagePreview ? "Change Main Image" : "Upload Main Image"}
                                    <input 
                                        type="file" 
                                        accept="image/*" 
                                        onChange={handleMainImageChange} 
                                        className="sr-only" 
                                    />
                                </label>
                            </div>

                            {newMainImagePreview && (
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleCancelNewMainImage}
                                        className="text-xs text-red-600 hover:text-red-800 underline"
                                    >
                                        Cancel & revert to original
                                    </button>
                                </div>
                            )}
                            <p className="text-xs text-gray-500">Supports JPG, PNG, WEBP up to 5MB. Will be updated when clicking Save Changes.</p>
                        </div>
                    </div>
                </div>

                {/* Existing Gallery Images Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Gallery Images ({currentImages.length})</h3>
                    {currentImages.length === 0 ? (
                        <p className="text-gray-500 italic">No additional gallery images attached.</p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {currentImages.map((img) => {
                                const isCurrentMain = currentMainImage && img.image && (currentMainImage.includes(img.image) || img.image.includes(currentMainImage));
                                return (
                                    <div key={img.id} className="relative group rounded-lg overflow-hidden border bg-gray-100 flex flex-col">
                                        <img 
                                            src={img.image} 
                                            alt="News Item" 
                                            className="w-full h-32 object-cover"
                                        />
                                        <div className="p-1.5 bg-white border-t flex items-center justify-between">
                                            {isCurrentMain ? (
                                                <span className="text-[11px] text-green-700 font-bold">⭐ Main Image</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => handleSetAsMainImage(img.id, img.image)}
                                                    className="text-[11px] text-blue-600 hover:text-blue-800 font-medium hover:underline"
                                                    title="Set this as the main post image"
                                                >
                                                    Set as Main
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => deleteExistingImage(img.id)}
                                                className="text-red-500 hover:text-red-700 p-1"
                                                title="Delete Image"
                                            >
                                                <MdDelete size={14} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Add New Images Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Add More Images</h3>
                     <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 transition-colors">
                        <div className="space-y-1 text-center">
                            <MdCloudUpload className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="flex text-sm text-gray-600">
                                <label htmlFor="new-news-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                    <span>Upload files</span>
                                    <input 
                                        id="new-news-upload" 
                                        name="new-news-upload" 
                                        type="file" 
                                        className="sr-only" 
                                        onChange={handleImageChange} 
                                        accept="image/*" 
                                        multiple 
                                    />
                                </label>
                                <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                        </div>
                    </div>
                    {/* New Images Preview */}
                     {newImages.length > 0 && (
                        <div className="mt-4 grid grid-cols-4 md:grid-cols-6 gap-4">
                            {newImages.map((file, index) => (
                                <div key={index} className="relative group">
                                    <div className="w-full h-20 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-center p-1 text-xs text-center break-all overflow-hidden text-blue-700">
                                        {file.name}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeNewImage(index)}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                                    >
                                        <MdDelete className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex justify-end pt-4 border-t">
                    <button
                        type="submit"
                        disabled={saving}
                        className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                        <MdSave className="mr-2" />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditNaiNews;
